import React, { FunctionComponent, useState } from 'react';
import { useCommonStyles } from '../CommonStyles';
import Card from '../UI/Card';
import { getCards, CardData, CardTitles } from '../Data/CardData';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useHasPremiumPlan } from 'hooks/premiumPlan';
import NotAvailable from '../NotAvailable/NotAvailable';
import SearchInput from '../components/SearchInput';
import ResultsNotFound from '../components/ResultsNotFound';
import Commissions from './Commissions';
import Tips from './Tips';
import TeamRevenue from './TeamRevenue';
import Productivity from './Productivity';
import store from 'store';

interface Props {
    title: string;
    category: string;
}

const Staff: FunctionComponent<Props> = ({ title, category }) => {
    const commonClasses = useCommonStyles();
    const hasPremiumPlan = useHasPremiumPlan();

    const [selectedCard, setSelectedCard] = useState<CardData | null>(null);
    const [searchText, setSearchText] = useState('');
    const [showSearch, setShowSearch] = useState(true);

    const handleCardClick = (card: CardData) => {
        setSelectedCard(card);
        setShowSearch(false);
    };

    const showDefaultView = () => {
        setSelectedCard(null);
        setShowSearch(true);
    };

    const marketplaceId = store.getState().marketplace.marketplace.id;
    const cards = getCards(marketplaceId);

    const filteredCards = cards.filter(
        card =>
            card.category === category &&
            card.active &&
            (card.title.toLowerCase().includes(searchText.toLowerCase()) ||
                card.description.toLowerCase().includes(searchText.toLowerCase()))
    );

    const handleClickSearch = (): void => {
        setSearchText('');
    };

    const renderContent = () => {
        if (!selectedCard) {
            return (
                <div className={commonClasses.cardsContent}>
                    {filteredCards
                        .filter(card => card.category === category)
                        .map((card, index) => (
                            <Card
                                key={index}
                                title={card.title}
                                subTitle={card.category}
                                description={card.description}
                                premium={card.premium}
                                icon={card.icon}
                                onClick={() => {
                                    handleCardClick(card);
                                }}
                            />
                        ))}
                </div>
            );
        }

        // Return different components based on selected Card
        switch (selectedCard.title) {
            case CardTitles.COMMISSIONS:
                return selectedCard.premium && !hasPremiumPlan ? (
                    <NotAvailable />
                ) : (
                    <Commissions description={selectedCard.description} />
                );
            case CardTitles.TIPS:
                return selectedCard.premium && !hasPremiumPlan ? (
                    <NotAvailable />
                ) : (
                    <Tips description={selectedCard.description} />
                );
            case CardTitles.TEAM_REVENUE:
                return selectedCard.premium && !hasPremiumPlan ? (
                    <NotAvailable />
                ) : (
                    <TeamRevenue description={selectedCard.description} />
                );
            case CardTitles.PRODUCTIVITY:
                return selectedCard.premium && !hasPremiumPlan ? (
                    <NotAvailable />
                ) : (
                    <Productivity description={selectedCard.description} />
                );
            default:
                return null;
        }
    };

    const reportsNotFound = () => {
        return (
            <div className={commonClasses.resultNotFound}>
                <ResultsNotFound title="No reports found" />
            </div>
        );
    };

    return (
        <div className={commonClasses.container}>
            <div className={commonClasses.contentInsights}>
                <div className={commonClasses.subViewTitle}>
                    {selectedCard && (
                        <FontAwesomeIcon
                            className={commonClasses.backIcon}
                            icon={faArrowLeft}
                            onClick={showDefaultView}
                        />
                    )}
                    <h2 className={commonClasses.titleDefault}>{selectedCard ? selectedCard.title : title}</h2>
                </div>
                {showSearch && (
                    <SearchInput
                        searchText={searchText}
                        setSearchText={setSearchText}
                        showSearch={showSearch}
                        handleClickSearch={handleClickSearch}
                    />
                )}
                {filteredCards.length === 0 && reportsNotFound()}
                {renderContent()}
            </div>
        </div>
    );
};

export default Staff;
